import { Link } from "react-router-dom";
import { Shield, Target, Eye, ArrowLeft } from "lucide-react";
import { SocialLinks } from "@/components/SocialLinks";
import logo from "@/assets/logo.png";
import SEO from "@/components/SEO";
import { ORGANIZATION_LD, SOFTWARE_APPLICATION_LD } from "@/lib/structuredData";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About GreyRock AI® — Forensic Communication Intelligence"
        description="Why we built GreyRock AI: a forensic-grade tool that decodes manipulation, gaslighting and coercive patterns so people can name what just happened to them."
        path="/about"
        jsonLd={[ORGANIZATION_LD, SOFTWARE_APPLICATION_LD]}
      />

      {/* Nav */}
      <nav className="relative z-20 flex items-center justify-between px-6 py-4 md:px-12 border-b border-glass-border bg-background/60 backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-3">
          <img src={logo} alt="GreyRock AI" className="w-10 h-10 object-contain drop-shadow-[0_0_12px_rgba(0,242,255,0.3)]" />
          <span className="text-xl font-bold tracking-tight">
            <span className="text-foreground">Grey</span>
            <span className="text-cyan">Rock</span>
            <span className="text-foreground"> AI</span>
            <sup className="text-[10px] text-muted-foreground">&reg;</sup>
          </span>
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </nav>

      <div className="relative z-10 container mx-auto px-6 py-12 max-w-2xl space-y-10">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            <span className="text-foreground">About </span>
            <span className="text-gradient-cyan">GreyRock AI&reg;</span>
          </h1>
          <p className="text-sm text-muted-foreground">Our mission &amp; purpose</p>
        </div>

        {/* Mission Statement */}
        <section className="glass-card p-6 md:p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Target className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg text-foreground">Our Mission</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed text-base">
            GreyRock AI&reg; is a forensic intelligence platform dedicated to decoding the complex patterns of high-conflict communication. We empower targets of manipulation with objective, data-driven insights to neutralize toxic dynamics and regain their emotional sovereignty.
          </p>
        </section>

        {/* What We Do */}
        <section className="glass-card p-6 md:p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Eye className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg text-foreground">What We Do</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            We provide AI-powered forensic text analysis that identifies manipulation patterns, scores toxicity, and generates neutralized responses — all in real-time, with complete privacy. Our platform includes rehearsal simulations, strategy vaults, and shareable forensic cards for creators and advocates.
          </p>
        </section>

        {/* Privacy Commitment */}
        <section className="glass-card p-6 md:p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-cyan/10">
              <Shield className="w-6 h-6 text-cyan" />
            </div>
            <h2 className="font-semibold text-lg text-foreground">Privacy First</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            We do not store your messages. Analysis happens in real-time and disappears when you close the tab. Your conversations stay private — always.
          </p>
        </section>

        {/* Social */}
        <div className="text-center space-y-3 pt-4">
          <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground/60 font-mono">Follow the Lab</p>
          <SocialLinks className="justify-center" />
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-glass-border py-8 px-6 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img src={logo} alt="GreyRock AI" className="w-6 h-6 object-contain opacity-60" />
          <span className="text-sm font-medium text-muted-foreground">GreyRockAI.app&reg;</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
          <Link to="/about" className="text-muted-foreground hover:text-cyan transition-colors">About</Link>
          <span className="text-muted-foreground/40">&bull;</span>
          <Link to="/contact" className="text-muted-foreground hover:text-cyan transition-colors">Contact</Link>
          <span className="text-muted-foreground/40">&bull;</span>
          <Link to="/privacy" className="text-muted-foreground hover:text-cyan transition-colors">Privacy Policy</Link>
          <span className="text-muted-foreground/40">&bull;</span>
          <Link to="/terms" className="text-muted-foreground hover:text-cyan transition-colors">Terms of Service</Link>
          <span className="text-muted-foreground/40">&bull;</span>
          <Link to="/refunds" className="text-muted-foreground hover:text-cyan transition-colors">Refund Policy</Link>
        </div>
        <p className="text-sm text-muted-foreground pt-2">&copy; 2026 GreyRock AI&reg;. All Rights Reserved.</p>
      </footer>
    </div>
  );
};

export default About;
